/**
 * Clicks and keys inside a sandboxed email iframe.
 *
 * The sandbox runs no scripts and opens no popups, so a click would load the
 * link inside the frame, where the portal cannot start and the frame goes blank.
 * The parent opens the link instead, so the sandbox stays as tight as it is and
 * the only thing an email can reach is an ordinary web address.
 *
 * @link ./EmailPreviewFrame for the frame this is attached to.
 */

/** Only ordinary web links leave the frame: never `javascript:` or `data:`. */
const WEB_LINK = /^https?:\/\//i;

/**
 * Open the frame's links in a new tab.
 *
 * @param frame - the loaded iframe; needs `allow-same-origin` to be reachable.
 */
export function openLinksInNewTab(frame: HTMLIFrameElement): void {
  frame.contentDocument?.addEventListener('click', (event) => {
    const target = event.target as Element | null;
    const link = target?.closest?.('a[href]');
    if (!link) return;
    event.preventDefault();
    const href = (link as HTMLAnchorElement).href;
    if (WEB_LINK.test(href)) window.open(href, '_blank', 'noopener,noreferrer');
  });
}

/**
 * Pass Escape out of the frame. Once someone clicks inside, the key lands on the
 * frame's own document and a dialog around it never hears it.
 *
 * @param frame - the loaded iframe; needs `allow-same-origin` to be reachable.
 */
export function forwardEscape(frame: HTMLIFrameElement): void {
  frame.contentDocument?.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    frame.ownerDocument.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
    );
  });
}
